import { useState, useEffect, useRef } from 'react';
import API from './api';

const CATEGORIES = [
  { key: 'ims', label: 'IMS', desc: 'Company IMS procedures' },
  { key: 'ism', label: 'ISM', desc: 'ISM Code documents' },
  { key: 'isps', label: 'ISPS', desc: 'ISPS Code documents' },
  { key: 'mlc', label: 'MLC', desc: 'MLC Requirements' },
  { key: 'flag', label: 'Flag', desc: 'Flag State requirements' },
  { key: 'iso9001', label: 'ISO 9001:2015', desc: 'Quality Management System' },
  { key: 'iso14001', label: 'ISO 14001:2015', desc: 'Environmental Management System' },
  { key: 'other', label: 'Other', desc: 'Other references' },
];

function formatSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export default function LibraryManager({ setPage, onLogout }) {
  const [stats, setStats] = useState(null);
  const [files, setFiles] = useState({});
  const [activeCategory, setActiveCategory] = useState('ims');
  const [uploading, setUploading] = useState(false);
  const [uploadMode, setUploadMode] = useState('files');
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const fileInputRef = useRef();
  const zipInputRef = useRef();

  useEffect(() => {
    fetchData();
    // Tarayıcının dropzone dışına bırakılan dosyayı açmasını engelle
    const preventBrowserOpen = (e) => { e.preventDefault(); };
    document.addEventListener('dragover', preventBrowserOpen);
    document.addEventListener('drop', preventBrowserOpen);
    return () => {
      document.removeEventListener('dragover', preventBrowserOpen);
      document.removeEventListener('drop', preventBrowserOpen);
    };
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, filesRes] = await Promise.all([
        API.get('/library/stats'),
        API.get('/library/files'),
      ]);
      setStats(statsRes.data);
      // Sunucu string[] veya {name,size}[] dönebilir — normalize et
      const normalized = {};
      for (const [cat, items] of Object.entries(filesRes.data)) {
        normalized[cat] = (items || []).map(f =>
          typeof f === 'string' ? { name: f, size: 0 } : f
        );
      }
      setFiles(normalized);
    } catch (e) {
      if (e.response?.status === 401) onLogout();
    }
  };

  const handleFileUpload = async (e, droppedFiles = null) => {
    const selectedFiles = droppedFiles || Array.from(e.target.files);
    if (!selectedFiles.length) return;
    setUploading(true);
    setError('');
    setProgress(`Uploading ${selectedFiles.length} file(s)...`);

    const formData = new FormData();
    selectedFiles.forEach(f => formData.append('files', f));

    try {
      const res = await API.post(`/library/upload?category=${activeCategory}`, formData, {
        timeout: 300000,
      });
      const saved = res.data.saved || [];
      setProgress(`✓ ${saved.length} file(s) uploaded: ${saved.join(', ')}`);
      fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError('Upload failed: ' + (Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : detail || e.message));
    } finally {
      setUploading(false);
      if (e?.target) e.target.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (!droppedFiles.length) return;
    if (uploadMode === 'zip') {
      const zipFiles = droppedFiles.filter(f => f.name.endsWith('.zip'));
      if (zipFiles.length) handleZipUpload(null, zipFiles[0]);
    } else {
      handleFileUpload(null, droppedFiles);
    }
  };

  const handleZipUpload = async (e, droppedFile = null) => {
    const file = droppedFile || e?.target.files[0];
    if (!file) return;
    setUploading(true);
    setError('');
    setProgress(`Uploading ZIP: ${file.name}...`);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await API.post(`/library/upload-zip?category=${activeCategory}`, formData, {
        timeout: 600000,
      });
      setProgress(`✓ Extracted ${res.data.extracted?.length || 0} files.`);
      fetchData();
    } catch (e) {
      setError('ZIP upload failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
      if (e?.target) e.target.value = '';
    }
  };

  const handleDelete = async (category, filename) => {
    if (!window.confirm(`Delete ${filename}?`)) return;
    try {
      await API.delete(`/library/files/${category}/${encodeURIComponent(filename)}`);
      fetchData();
    } catch (e) {
      setError('Delete failed.');
    }
  };

  const handleReindex = async () => {
    setUploading(true);
    setProgress('Re-indexing...');
    try {
      const res = await API.post('/library/reindex');
      setProgress(`✓ Re-indexed ${res.data.reindexed} files.`);
      fetchData();
    } catch (e) {
      setError('Reindex failed.');
    } finally {
      setUploading(false);
    }
  };

  const currentFiles = files[activeCategory] || [];
  const totalFiles = Object.values(files).reduce((a, b) => a + b.length, 0);

  return (
    <div style={s.wrapper}>
      <header style={s.header}>
        <button style={s.backBtn} onClick={() => setPage('dashboard')}>← Back</button>
        <div style={s.headerTitle}>Library Manager</div>
        <div style={s.headerRight}>
          <button style={s.guideBtn} onClick={() => setShowGuide(true)}>? How to Guide</button>
          <div style={s.statBadge}>
            {stats?.index_stats?.total_chunks || 0} chunks indexed
          </div>
          <div style={s.statBadge}>{totalFiles} files</div>
          <button style={s.reindexBtn} onClick={handleReindex} disabled={uploading}>
            ↺ Re-index
          </button>
        </div>
      </header>

      {showGuide && (
        <div style={s.overlay} onClick={() => setShowGuide(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <div>
                <div style={s.modalTitle}>Library Manager — How to Guide</div>
                <div style={s.modalSub}>Document Repository & AI Search Index</div>
              </div>
              <button style={s.modalClose} onClick={() => setShowGuide(false)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.gSection}>
                <div style={s.gTitle}>What is the Library?</div>
                <div style={s.gText}>The Library is the platform's <strong style={s.hl}>regulatory and procedural document repository</strong>. All uploaded files are indexed in ChromaDB vector database and become searchable by the AI agents across the platform — especially Vessel Audit Management and QDMS Analyzer.</div>
              </div>
              <div style={s.gSection}>
                <div style={s.gTitle}>Categories</div>
                {[
                  { k:'ISM', desc:'ISM Code documents, Safety Management System, company procedures, ship procedures, checklists.' },
                  { k:'ISPS', desc:'ISPS Code documents, Ship Security Plan, port security assessments, security records.' },
                  { k:'MLC', desc:'Maritime Labour Convention documents, seafarer employment agreements, rest hour records, medical certificates.' },
                  { k:'FLAG', desc:'Flag state requirements, statutory certificates, recognized organization requirements.' },
                  { k:'IMS', desc:'Integrated Management System documents (cross-referenced with QDMS).' },
                  { k:'ISO 9001', desc:'Quality Management System documents, process procedures, audit records.' },
                  { k:'ISO 14001', desc:'Environmental Management System documents, environmental aspects, legal register.' },
                ].map(c => (
                  <div key={c.k} style={s.catRow}>
                    <span style={s.catBadge}>{c.k}</span>
                    <span style={s.gText}>{c.desc}</span>
                  </div>
                ))}
              </div>
              <div style={s.gSection}>
                <div style={s.gTitle}>Step-by-Step</div>
                {[
                  { n:'1', t:'Select a category', d:'Click the category name in the left sidebar to switch to it.' },
                  { n:'2', t:'Upload documents', d:'Drag & drop files or click "Upload Files". Accepted: PDF, DOCX, XLSX, PPTX, TXT. Multiple files can be uploaded at once.' },
                  { n:'3', t:'Documents are indexed automatically', d:'Each file is split into chunks and indexed in ChromaDB. The chunk count is shown in the header.' },
                  { n:'4', t:'Delete if needed', d:'Hover over a file and click the delete button. The index is updated automatically.' },
                ].map(step => (
                  <div key={step.n} style={s.step}>
                    <div style={s.stepN}>{step.n}</div>
                    <div><div style={s.stepT}>{step.t}</div><div style={s.gText}>{step.d}</div></div>
                  </div>
                ))}
              </div>
              <div style={s.gSection}>
                <div style={s.gTitle}>Important Notes</div>
                <div style={s.noteList}>
                  {['Upload relevant ISM procedures before using Vessel Audit Management — agents reference them automatically.',
                    'The more complete your Library, the more precise the AI regulation references will be.',
                    'If search results seem stale, click Re-index to rebuild the ChromaDB index.',
                    'Admin required to upload and delete; all users can view file lists.',
                  ].map((n,i) => <div key={i} style={s.noteItem}><span style={s.noteDot}>—</span><span>{n}</span></div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <main style={s.main}>
        <div style={s.layout}>

          {/* LEFT — Categories */}
          <div style={s.leftCol}>
            <div style={s.card}>
              <div style={s.cardTitle}>CATEGORIES</div>
              {CATEGORIES.map(cat => (
                <div
                  key={cat.key}
                  style={{ ...s.catItem, ...(activeCategory === cat.key ? s.catItemActive : {}) }}
                  onClick={() => setActiveCategory(cat.key)}
                >
                  <div style={s.catLeft}>
                    <div style={s.catLabel}>{cat.label}</div>
                    <div style={s.catDesc}>{cat.desc}</div>
                  </div>
                  <div style={s.catCount}>
                    {(files[cat.key] || []).length}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — Files */}
          <div style={s.rightCol}>

            {/* Upload Section */}
            <div style={s.card}>
              <div style={s.cardTitleRow}>
                <div style={s.cardTitle}>
                  UPLOAD TO: {activeCategory.toUpperCase()}
                </div>
                <div style={s.modeToggle}>
                  <button
                    style={{ ...s.modeBtn, ...(uploadMode === 'files' ? s.modeBtnActive : {}) }}
                    onClick={() => setUploadMode('files')}
                  >Files</button>
                  <button
                    style={{ ...s.modeBtn, ...(uploadMode === 'zip' ? s.modeBtnActive : {}) }}
                    onClick={() => setUploadMode('zip')}
                  >ZIP</button>
                </div>
              </div>

              {uploadMode === 'files' ? (
                <div
                  style={{ ...s.dropZone, ...(dragOver ? s.dropZoneActive : {}) }}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
                  onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
                  onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
                  onDrop={handleDrop}
                >
                  <div style={s.dropIcon}>{dragOver ? '📂' : '📁'}</div>
                  <div style={s.dropText}>{dragOver ? 'Release to upload' : 'Click to browse or drop files here'}</div>
                  <div style={s.dropNote}>PDF, Word, Excel, TXT — all formats supported</div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                    accept="*/*"
                  />
                </div>
              ) : (
                <div
                  style={{ ...s.dropZone, ...(dragOver ? s.dropZoneActive : {}) }}
                  onClick={() => zipInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
                  onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
                  onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
                  onDrop={handleDrop}
                >
                  <div style={s.dropIcon}>{dragOver ? '📂' : '🗜️'}</div>
                  <div style={s.dropText}>{dragOver ? 'Release to upload ZIP' : 'Click to upload ZIP archive'}</div>
                  <div style={s.dropNote}>All files inside ZIP will be extracted and indexed</div>
                  <input
                    ref={zipInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={handleZipUpload}
                    accept=".zip"
                  />
                </div>
              )}

              {uploading && (
                <div style={s.progressBar}>
                  <div style={s.progressText}>{progress}</div>
                </div>
              )}
              {!uploading && progress && (
                <div style={s.successMsg}>{progress}</div>
              )}
              {error && <div style={s.errorMsg}>{error}</div>}
            </div>

            {/* File List */}
            <div style={s.card}>
              <div style={s.cardTitleRow}>
                <div style={s.cardTitle}>
                  {activeCategory.toUpperCase()} — {currentFiles.length} FILE{currentFiles.length !== 1 ? 'S' : ''}
                </div>
              </div>

              {currentFiles.length === 0 ? (
                <div style={s.emptyFiles}>No files uploaded yet.</div>
              ) : (
                <div style={s.fileList}>
                  {currentFiles.map(f => (
                    <div key={f.name} style={s.fileItem}>
                      <div style={s.fileIcon}>
                        {f.name.endsWith('.pdf') ? '📄' :
                         f.name.endsWith('.docx') || f.name.endsWith('.doc') ? '📝' :
                         f.name.endsWith('.xlsx') || f.name.endsWith('.xls') ? '📊' : '📃'}
                      </div>
                      <div style={s.fileName}>{f.name}</div>
                      <div style={s.fileSize}>{formatSize(f.size)}</div>
                      <button
                        style={s.deleteBtn}
                        onClick={() => handleDelete(activeCategory, f.name)}
                      >✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

const s = {
  wrapper: { minHeight: '100vh', background: 'var(--navy)' },
  header: {
    background: 'var(--navy-mid)', borderBottom: '1px solid var(--navy-border)',
    padding: '0 40px', height: '64px', display: 'flex', alignItems: 'center', gap: '20px',
  },
  backBtn: { background: 'transparent', border: 'none', color: 'var(--grey)', cursor: 'pointer', fontSize: '13px' },
  headerTitle: { fontSize: '16px', fontWeight: '700', flex: 1 },
  headerRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  statBadge: {
    fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--green)',
    border: '1px solid rgba(0,196,140,0.3)', padding: '4px 10px', borderRadius: '4px',
  },
  reindexBtn: {
    background: 'transparent', border: '1px solid var(--navy-light)',
    color: 'var(--grey)', padding: '6px 14px', borderRadius: '6px',
    fontSize: '12px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  main: { padding: '40px', maxWidth: '1100px', margin: '0 auto' },
  layout: { display: 'grid', gridTemplateColumns: '280px 1fr', gap: '24px' },
  leftCol: {},
  rightCol: { display: 'flex', flexDirection: 'column', gap: '20px' },
  card: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
    borderRadius: '12px', padding: '24px',
  },
  cardTitle: {
    fontFamily: 'var(--font-mono)', fontSize: '11px', fontWeight: '600',
    letterSpacing: '2px', color: 'var(--gold)', marginBottom: '16px',
  },
  cardTitleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  catItem: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 14px', borderRadius: '8px', cursor: 'pointer',
    marginBottom: '6px', border: '1px solid transparent', transition: 'all 0.2s',
  },
  catItemActive: { background: 'var(--navy)', border: '1px solid var(--navy-light)' },
  catLeft: {},
  catLabel: { fontSize: '13px', fontWeight: '600' },
  catDesc: { fontSize: '11px', color: 'var(--grey)', marginTop: '2px' },
  catCount: {
    fontFamily: 'var(--font-mono)', fontSize: '13px', fontWeight: '600',
    color: 'var(--gold)', minWidth: '24px', textAlign: 'right',
  },
  modeToggle: { display: 'flex', gap: '4px' },
  modeBtn: {
    background: 'transparent', border: '1px solid var(--navy-light)',
    color: 'var(--grey)', padding: '5px 14px', borderRadius: '5px',
    fontSize: '12px', cursor: 'pointer',
  },
  modeBtnActive: { borderColor: 'var(--gold)', color: 'var(--gold)' },
  dropZone: {
    border: '2px dashed var(--navy-light)', borderRadius: '10px',
    padding: '40px 24px', textAlign: 'center', cursor: 'pointer',
    transition: 'all 0.2s',
  },
  dropZoneActive: {
    border: '2px dashed var(--gold)',
    background: 'rgba(201,168,76,0.06)',
  },
  dropIcon: { fontSize: '32px', marginBottom: '10px' },
  dropText: { fontSize: '14px', fontWeight: '600', marginBottom: '6px' },
  dropNote: { fontSize: '12px', color: 'var(--grey)' },
  progressBar: {
    marginTop: '14px', background: 'var(--navy)', borderRadius: '8px', padding: '12px 16px',
  },
  progressText: { fontSize: '12px', color: 'var(--gold)', fontFamily: 'var(--font-mono)' },
  successMsg: {
    marginTop: '14px', background: 'var(--green-dim)', border: '1px solid rgba(0,196,140,0.3)',
    borderRadius: '8px', padding: '12px 16px', fontSize: '12px', color: 'var(--green)',
    fontFamily: 'var(--font-mono)',
  },
  errorMsg: {
    marginTop: '14px', background: 'rgba(232,0,29,0.1)', border: '1px solid rgba(232,0,29,0.3)',
    borderRadius: '8px', padding: '12px 16px', fontSize: '12px', color: '#ff6b7a',
  },
  emptyFiles: { textAlign: 'center', color: 'var(--grey)', padding: '32px', fontSize: '13px' },
  fileList: { display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '400px', overflowY: 'auto' },
  fileItem: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '10px 12px', background: 'var(--navy)', borderRadius: '8px',
    border: '1px solid var(--navy-border)',
  },
  fileIcon: { fontSize: '16px', flexShrink: 0 },
  fileName: { flex: 1, fontSize: '12px', fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  fileSize: { fontSize: '11px', color: 'var(--grey)', fontFamily: 'var(--font-mono)', flexShrink: 0 },
  deleteBtn: {
    background: 'transparent', border: 'none', color: 'var(--grey)',
    cursor: 'pointer', fontSize: '14px', padding: '2px 6px', flexShrink: 0,
  },
  guideBtn: { background:'transparent', border:'1px solid var(--navy-light)', color:'var(--grey)', padding:'5px 12px', borderRadius:6, fontSize:11, fontWeight:600, cursor:'pointer', fontFamily:'var(--font-mono)', whiteSpace:'nowrap' },
  overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:24 },
  modal: { background:'#111d2e', border:'1px solid #1e2d40', borderRadius:16, width:'100%', maxWidth:700, maxHeight:'82vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 24px 80px rgba(0,0,0,0.6)' },
  modalHeader: { display:'flex', alignItems:'flex-start', justifyContent:'space-between', padding:'22px 26px 18px', borderBottom:'1px solid #1e2d40', flexShrink:0 },
  modalTitle: { fontSize:15, fontWeight:700, color:'#e8edf4', marginBottom:4 },
  modalSub: { fontSize:11, fontFamily:'var(--font-mono)', color:'var(--gold)', letterSpacing:1 },
  modalClose: { background:'transparent', border:'none', color:'var(--grey)', fontSize:16, cursor:'pointer', padding:'0 0 0 16px', lineHeight:1 },
  modalBody: { overflowY:'auto', padding:'22px 26px', display:'flex', flexDirection:'column', gap:22 },
  gSection: {},
  gTitle: { fontFamily:'var(--font-mono)', fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--gold)', marginBottom:10, textTransform:'uppercase' },
  gText: { fontSize:12, color:'#8a9ab0', lineHeight:1.7 },
  hl: { color:'#e8edf4' },
  catRow: { display:'flex', gap:10, alignItems:'flex-start', marginBottom:8 },
  catBadge: { flexShrink:0, fontFamily:'var(--font-mono)', fontSize:10, fontWeight:700, color:'var(--gold)', border:'1px solid rgba(201,168,76,0.35)', padding:'2px 8px', borderRadius:4, whiteSpace:'nowrap' },
  step: { display:'flex', gap:12, alignItems:'flex-start', marginBottom:10 },
  stepN: { flexShrink:0, width:26, height:26, borderRadius:'50%', background:'rgba(201,168,76,0.12)', border:'1px solid rgba(201,168,76,0.3)', color:'var(--gold)', fontSize:11, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-mono)' },
  stepT: { fontSize:13, fontWeight:600, color:'#e8edf4', marginBottom:3 },
  noteList: { display:'flex', flexDirection:'column', gap:7 },
  noteItem: { display:'flex', gap:8, fontSize:12, color:'#8a9ab0', lineHeight:1.6 },
  noteDot: { color:'var(--gold)', flexShrink:0 },
};
